import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus(null);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
      setSubmitting(false);
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d9tg4tv8l97n3eas39cg',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        }
      );

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // raw text response
      }

      const serverMsg =
        (parsed?.meta as Record<string, string>)?.message ||
        (parsed?.message as string) ||
        responseText ||
        '';

      if (
        !response.ok ||
        ((parsed?.code as string) && (parsed?.code as string) !== 'OK') ||
        serverMsg.toLowerCase().includes('spam')
      ) {
        setFormStatus({
          type: 'error',
          message: serverMsg || '提交失败，请稍后再试。',
        });
      } else {
        setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
        form.reset();
      }
    } catch {
      setFormStatus({
        type: 'error',
        message: '网络错误，请检查连接后重试。',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 邪娠娼馆世界观指南',
            description:
              '邪娠娼馆世界观指南是一个由资深读者团队创建的暗黑情色文学深度解析平台。我们致力于为读者提供最全面、最系统的世界观分析。',
            url: 'https://hnbocong.cn/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '邪娠娼馆世界观指南',
              url: 'https://hnbocong.cn/',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://hnbocong.cn/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
              },
            ],
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <section className="py-20 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-16">
                <span className="inline-block text-xs font-label text-primary-400 uppercase tracking-widest mb-3">
                  关于我们
                </span>
                <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-50 mb-4">
                  暗影中的引路人
                </h1>
                <p className="text-foreground-400 max-w-2xl mx-auto leading-relaxed text-sm md:text-base">
                  我们是一群痴迷于「邪娠娼馆」世界的深度读者，
                  致力于为每一位探索者提供最全面、最系统的世界观指南
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
                <div className="p-6 rounded-xl bg-background-100 border border-background-300/30">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 border border-primary-500/20 mb-4">
                    <i className="ri-lightbulb-line text-lg text-primary-400"></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-100 mb-3">
                    我们的使命
                  </h3>
                  <p className="text-sm text-foreground-400 leading-relaxed">
                    打破信息碎片化的困境，将散落在原作各处的设定、伏笔和隐喻进行系统化整理，
                    让每一位读者都能在清晰的框架下深入理解这部复杂作品的精髓。
                    我们相信，优秀的文学作品值得被认真解读，而深度解读本身就是一种创作。
                  </p>
                </div>

                <div className="p-6 rounded-xl bg-background-100 border border-background-300/30">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/10 border border-accent-500/20 mb-4">
                    <i className="ri-team-line text-lg text-accent-400"></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-100 mb-3">
                    我们的团队
                  </h3>
                  <p className="text-sm text-foreground-400 leading-relaxed">
                    我们的核心编辑团队由文学研究者、资深读者和创意写作者组成。
                    每位成员都带着独特的视角和专业知识，从叙事学、符号学、心理学等多维度切入分析，
                    确保每一篇内容都经得起推敲。
                  </p>
                </div>
              </div>

              <div className="mb-20">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 text-center mb-12">
                  读者评价
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {[
                    {
                      text: '这是我见过最系统的「邪娠娼馆」世界观整理。每一篇文章都像一把钥匙，帮我打开了理解这部作品的新大门。',
                      name: '暗夜旅人',
                      role: '资深读者',
                    },
                    {
                      text: '网站的角色图鉴做得太棒了！以前一直搞不清的人物关系，现在一目了然。尤其是契约魔法的体系梳理，让我对作品有了全新的认识。',
                      name: '月下独酌',
                      role: '奇幻文学爱好者',
                    },
                    {
                      text: '创作解析系列是我最喜欢的部分。从文学技法的角度去重新审视那些我曾经只是「读过」的段落，才发现作者藏了多少精妙的心思。',
                      name: '笔尖上的幽灵',
                      role: '同人创作者',
                    },
                  ].map((review, idx) => (
                    <div
                      key={idx}
                      className="p-6 rounded-xl bg-background-100 border border-background-300/30"
                    >
                      <div className="flex items-center gap-1 mb-4">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <i key={star} className="ri-star-fill text-accent-400 text-sm"></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-300 leading-relaxed mb-4 italic">
                        &ldquo;{review.text}&rdquo;
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                          <i className="ri-user-line text-secondary-500 text-sm"></i>
                        </div>
                        <div>
                          <div className="text-xs font-label font-medium text-foreground-200">
                            {review.name}
                          </div>
                          <div className="text-xs text-foreground-600">{review.role}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-20">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 text-center mb-4">
                  内容声明
                </h2>
                <div className="max-w-2xl mx-auto p-6 rounded-xl bg-background-100 border border-background-300/30">
                  <div className="space-y-3 text-sm text-foreground-400 leading-relaxed">
                    <p>
                      <strong className="text-foreground-200">非官方性质：</strong>
                      本站是一个由粉丝自发创建的内容分析平台，与「邪娠娼馆」的原作者及出版方无任何商业关联或授权关系。
                    </p>
                    <p>
                      <strong className="text-foreground-200">内容定位：</strong>
                      本站所有文章均为基于原文文本的文学分析与创作解读，属于学术研究性质的二次创作范畴。我们尊重原作的著作权，
                      所有引用均标注了来源。
                    </p>
                    <p>
                      <strong className="text-foreground-200">读者须知：</strong>
                      本站内容面向成年读者。作品本身含有成人向内容，建议未满18周岁的读者在监护人指导下选择性浏览。
                    </p>
                    <p>
                      <strong className="text-foreground-200">版权声明：</strong>
                      本站原创分析文章版权归本站所有。如需转载，请通过下方联系表单与我们取得联系并获得授权。
                    </p>
                  </div>
                </div>
              </div>

              <div id="contact">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 text-center mb-4">
                  联系我们
                </h2>
                <p className="text-foreground-400 text-center text-sm mb-10 max-w-lg mx-auto">
                  如果你有任何问题、建议或合作意向，欢迎通过下方表单与我们取得联系。
                  我们通常会在48小时内回复。
                </p>
                <div className="max-w-lg mx-auto">
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="p-6 md:p-8 rounded-xl bg-background-100 border border-background-300/30 space-y-5"
                  >
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="block text-sm font-label text-foreground-300 mb-2"
                      >
                        你的昵称 *
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-200 border border-background-300/60 text-foreground-200 text-sm placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                        placeholder="请输入你的昵称"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-email"
                        className="block text-sm font-label text-foreground-300 mb-2"
                      >
                        电子邮箱 *
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-200 border border-background-300/60 text-foreground-200 text-sm placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                        placeholder="请输入你的邮箱地址"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-subject"
                        className="block text-sm font-label text-foreground-300 mb-2"
                      >
                        主题
                      </label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        className="w-full px-4 py-2.5 rounded-lg bg-background-200 border border-background-300/60 text-foreground-200 text-sm placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                        placeholder="留言主题（选填）"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-message"
                        className="block text-sm font-label text-foreground-300 mb-2"
                      >
                        留言内容 *
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-4 py-2.5 rounded-lg bg-background-200 border border-background-300/60 text-foreground-200 text-sm placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                        placeholder="请输入你的留言，最多500字"
                      ></textarea>
                      <p className="text-xs text-foreground-600 mt-1">最多500个字符</p>
                    </div>

                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="hp-hidden"
                    />

                    {formStatus && (
                      <div
                        className={`text-sm p-3 rounded-lg ${
                          formStatus.type === 'success'
                            ? 'bg-accent-500/10 border border-accent-500/20 text-accent-300'
                            : 'bg-primary-500/10 border border-primary-500/20 text-primary-300'
                        }`}
                      >
                        {formStatus.message}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-label text-sm font-semibold hover:bg-primary-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {submitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          提交中...
                        </span>
                      ) : (
                        '发送留言'
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}